<template>
  <div
    v-if="recurrentTask"
    ref="taskEl"
    v-hotkey="shortcuts"
    class="recurrent-task-inline"
    :class="{ selected: isSelected, editing: isEditingTitle }"
    @click="select"
    @contextmenu.prevent.stop="showContextMenu"
    @dblclick="openTask($event)"
  >
    <TaskIcon :recurring="status" class="checkbox-recurring" />
    <RInput
      v-if="isEditingTitle"
      ref="rinputEl"
      :value="recurrentTask.title"
      class="smooth"
      @blur="inputBlur"
      @enter="inputBlur"
      @escape="inputBlur"
    />
    <span v-else class="title smooth">{{ recurrentTask.title }}</span>

    <NotesIcon
      v-if="recurrentTask.notes && recurrentTask.notes.blocks.length > 0"
      class="description"
    />
    <BaseButton
      v-if="!!parentTitle"
      :tabindex="-1"
      class="hierarchy"
      kind="auxiliary"
      :text="parentTitle"
      tooltip-display="ellipsis"
      :tooltip="parentTitle"
      @click="clickParent($event)"
    />
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, nextTick, ref, toRefs, watch } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { getRecurrentTaskShortcuts } from "@/components/object-shortcuts";
import { Recurrence, recurrenceStatus } from "@/components/recurrence";
import RInput from "@/components/RInput.vue";
import type { Shortcuts } from "@/components/shortcuts";
import { clickHierarchy } from "@/components/utils";
import { bindAsync, useReleaser } from "@/ref";
import { useParent } from "@/task";
import { makeMove } from "@/utils/move";
import { navigateToObject } from "@/utils/navigate";

export default defineComponent({
  components: { RInput, BaseButton, NotesIcon, TaskIcon },
  props: {
    recurrentTaskId: { type: String, required: true },
    isSelected: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
  },
  emits: {
    quitEditing: () => true,
    select: () => true,
    setSelectedTaskEl: (_el: HTMLElement) => true,
  },
  async setup(props, { emit }) {
    const { isEditing, isSelected, recurrentTaskId } = toRefs(props);
    const controller = inject.controller();
    const profile = inject.profile();
    const logger = inject.logger();
    const destinationPicker = inject.destinationPicker();
    const move = makeMove(controller, profile, logger, destinationPicker);
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const recurrencePicker = inject.recurrencePicker();
    const inDashboard = inject.inDashboard();
    const contextMenu = !inDashboard ? inject.contextMenu() : null;
    const router = inject.router();
    const simpleModal = inject.simpleModal();
    const menu = inject.menu();
    const isEditingTitle = computed(() => isEditing.value && isSelected.value);
    const rinputEl = ref<InstanceType<typeof RInput>>();
    const taskEl = ref<HTMLDivElement>();
    watch(isSelected, (v) => {
      if (!v || !taskEl.value) return;
      else emit("setSelectedTaskEl", taskEl.value);
    });
    const releaser = useReleaser();
    const recurrentTaskPromise = releaser.add(
      bindAsync(recurrentTaskId, (id) =>
        Promise.resolve(controller.taskRecurrent.get(id))
      )
    );
    const parentTitlePromise = releaser.add(
      recurrentTaskPromise.then((task) =>
        useParent(controller, task).then(({ parentTitle }) => parentTitle)
      )
    );
    const [parentTitle, recurrentTask] = await Promise.all([
      parentTitlePromise,
      recurrentTaskPromise,
    ]);
    const status = computed<Recurrence>(() =>
      recurrenceStatus(recurrentTask.value)
    );
    const openTask = (mouseEvent?: MouseEvent) => {
      if (recurrentTask.value) {
        navigateToObject(
          router,
          controller,
          {
            kind: "task-rec",
            id: recurrentTask.value.id,
          },
          mouseEvent
        );
      }
    };
    const clickParent = async (mouseEvent?: MouseEvent) =>
      recurrentTask.value?.parent
        ? await clickHierarchy(
            controller,
            router,
            recurrentTask.value.parent,
            mouseEvent
          )
        : null;
    const inputBlur = () => {
      if (isEditingTitle.value) {
        const text = rinputEl.value?.$el.innerText;
        if (text.trimEnd() !== recurrentTask.value?.title.trimEnd())
          controller.taskRecurrent.update(
            recurrentTaskId.value,
            {
              title: text,
            },
            "app"
          );
        emit("quitEditing");
      }
    };
    const select = () => {
      if (isEditingTitle.value) return;
      emit("select");
    };
    releaser.defer(
      watch(
        isEditingTitle,
        (v) => {
          nextTick(() => {
            if (v && isSelected.value && rinputEl.value) {
              // Force caret placement; without `force=true` the caret may default to index 0
              // (e.g. when entering edit mode via Enter) and won't be moved to the end.
              rinputEl.value.focus(false, true);
            }
          });
        },
        { immediate: true }
      )
    );
    const showContextMenu = (e: MouseEvent) => {
      if (recurrentTask.value && contextMenu) {
        select();
        contextMenu.value.show(
          { type: "task-rec", value: recurrentTask.value },
          e
        );
      }
    };
    const shortcuts = computed<Shortcuts>(() => {
      if (isSelected.value && recurrentTask.value)
        return {
          ...getRecurrentTaskShortcuts(
            recurrentTask.value,
            {
              isInputFocused,
              isModalShown,
              controller,
              logger,
              profile,
              move,
              router,
              simpleModal,
              menu,
              // The recurrence picker must be injected even though
              // it is not usable directly in this component, hence
              // the empty onSelect function
              recurrencePickerInject: {
                component: recurrencePicker,
                triggerEl: ref<HTMLElement>(),
                onSelect: () => {},
              },
            },
            { closeContextMenu: contextMenu?.value.hide }
          ),
        };
      else return {};
    });
    return {
      clickParent,
      inputBlur,
      isEditingTitle,
      openTask,
      parentTitle,
      recurrentTask,
      rinputEl,
      select,
      shortcuts,
      showContextMenu,
      status,
      taskEl,
    };
  },
});
</script>
<style scoped lang="scss">
.recurrent-task-inline {
  display: flex;
  flex-direction: row;
  border: 1px solid transparent;
  column-gap: 8px;
  align-items: center;
  padding: 6px 16px;
  border-radius: 3px;
  min-height: 28px;
  &:not(.editing) {
    height: 28px;
  }
  &:hover {
    @include themed() {
      border: 1px solid t($border-inline-element-hover);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
  }
  &.selected {
    @include themed() {
      border: 1px solid t($border-inline-element-selected);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
    &.editing {
      @include themed() {
        background: t($bg-inline-element-editing);
        border: 1px solid t($bg-inline-element-editing);
      }
      :deep(.task-icon) .recurring {
        @include themed() {
          background: t($bg-inline-element-editing);
        }
      }
    }
  }
  &.editing {
    :deep(.r-input) {
      display: flex;
      @extend %font-body-regular;
      flex-shrink: 2;
      width: fit-content;
      @include themed() {
        color: t($primary);
      }
      .input {
        word-break: break-word;
      }
    }
  }
  .checkbox-recurring {
    margin-top: 4px;
  }
  .title {
    @extend %font-body-regular;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .smooth {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: antialiased;
    font-smoothing: antialiased;
  }
  .description {
    flex-shrink: 0;
  }
}
</style>
